// src/ui/Avatar3D.jsx — a live avatar on a <canvas>.
//
// 🟢 THE SAME SCENE THE PHONE DRAWS. services/avatarScene.js is generated from
//    the phone's TypeScript (tools/gen-twins.mjs in allo-app) — the framing, the
//    circle mask, the peek, the wave, the lights are one code path, so a
//    portrait rendered on either client is the portrait the other draws live.
//    What this file adds is what a browser has and a phone does not: a canvas,
//    a WebGL2 context, pointer events, and toBlob() for the snapshot.
//
// 🟢 RENDER ON DEMAND: a frame is drawn only when the scene says something
//    moved. A page of still avatars costs nothing after the first frame.

import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback } from 'react';
import * as THREE from 'three';
import { AvatarScene, cloneAvatar, liveViewSize } from '../services/avatarScene';
import { hasAvatar } from '../services/avatar3d';
/* the figure: assembled from our catalogue, shared by every view on the page */
import { loadAvatarModel, warmAvatar } from '../services/avatarKitLoader';
export { loadAvatarModel, warmAvatar };


const Avatar3D = forwardRef(function Avatar3D(props, ref) {
  const {
    avatar, mode = 'portrait', size = 50, width, height, fallback = null, interactive = true,
    returnAfterMs = 900, backdrop = true, onReady, onError, onSettled, className, style, snapshotable = false,
  } = props;
  const viewW = mode === 'portrait' ? size : (width || 200);
  const viewH = mode === 'portrait' ? liveViewSize(size).height : (height || 300);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const rafRef = useRef(null);
  const aliveRef = useRef(true);
  const readyRef = useRef(false);
  const settledRef = useRef(true);
  const returnTimer = useRef(null);
  const [ready, setReady] = useState(false);
  const [gen, setGen] = useState(0);

  const draw = useCallback(() => { const s = sceneRef.current; if (s) s.render(); }, []);
  const loop = useCallback(() => {
    rafRef.current = null;
    const s = sceneRef.current;
    if (!s || !aliveRef.current) return;
    if (s.update()) draw();
    if (s.isAnimating()) { settledRef.current = false; rafRef.current = requestAnimationFrame(loop); }
    else if (!settledRef.current) { settledRef.current = true; onSettled && onSettled(); }
  }, [draw, onSettled]);
  const kick = useCallback(() => { if (rafRef.current == null && aliveRef.current) rafRef.current = requestAnimationFrame(loop); }, [loop]);

  /* the context and the scene live as long as the view's shape does */
  useEffect(() => {
    aliveRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas || !hasAvatar({ avatar })) return undefined;
    const pr = Math.min(2, window.devicePixelRatio || 1);
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: !!snapshotable, powerPreference: 'low-power' });
    } catch (e) { onError && onError(e); return undefined; }
    rendererRef.current = renderer;
    const scene = new AvatarScene(renderer, { mode, width: viewW, height: viewH, diameter: size, pixelRatio: pr, backdrop });
    sceneRef.current = scene;
    setGen((g) => g + 1);
    return () => {
      aliveRef.current = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (returnTimer.current) clearTimeout(returnTimer.current);
      scene.dispose(); sceneRef.current = null;
      renderer.dispose(); rendererRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!avatar, mode, size, viewW, viewH, backdrop]);

  /* 🟢 A NEW LOOK IS A NEW MODEL IN THE SAME SCENE — the builder's preview
     follows a swatch tap without tearing down the context. */
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !hasAvatar({ avatar })) return undefined;
    let cancelled = false;
    loadAvatarModel(avatar).then((loaded) => {
      if (cancelled || sceneRef.current !== scene) return;
      scene.setModel(cloneAvatar(loaded.root), loaded.landmarks);
      scene.update(); draw();
      readyRef.current = true; setReady(true);
      onReady && onReady();
      kick();
    }).catch((e) => { if (!cancelled) onError && onError(e); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatar && avatar.id, gen]);

  const scheduleReturn = useCallback(() => {
    if (returnTimer.current) clearTimeout(returnTimer.current);
    if (!returnAfterMs) return;
    returnTimer.current = setTimeout(() => { sceneRef.current && sceneRef.current.returnToFront(); kick(); }, returnAfterMs);
  }, [returnAfterMs, kick]);
  const dragBy = useCallback((dx) => { const s = sceneRef.current; if (!s) return; if (returnTimer.current) clearTimeout(returnTimer.current); s.dragBy(dx); kick(); }, [kick]);
  const dragEnd = useCallback((vx) => { const s = sceneRef.current; if (!s) return; s.dragEnd(vx); kick(); scheduleReturn(); }, [kick, scheduleReturn]);

  useImperativeHandle(ref, () => ({
    peek() { sceneRef.current && sceneRef.current.peek(); kick(); },
    nod() { sceneRef.current && sceneRef.current.nod(); kick(); },
    lookAround() { sceneRef.current && sceneRef.current.lookAround(); kick(); },
    blink() { sceneRef.current && sceneRef.current.blink(); kick(); },
    wave() { sceneRef.current && sceneRef.current.wave(); kick(); },
    returnToFront() { sceneRef.current && sceneRef.current.returnToFront(); kick(); },
    dragBy, dragEnd,
    ready() { return readyRef.current; },
    /** the portrait at rest as a PNG Blob — a square, the circle's own box */
    snapshot() {
      return new Promise((resolve) => {
        const s = sceneRef.current, c = canvasRef.current;
        if (!s || !c) return resolve(null);
        s.poseForSnapshot(); s.render();
        if (mode !== 'portrait') return c.toBlob((b) => resolve(b), 'image/png');
        const side = Math.min(c.width, c.height);
        const out = document.createElement('canvas');
        out.width = side; out.height = side;
        const ctx = out.getContext('2d');
        // the circle sits at the bottom of the taller view
        ctx.drawImage(c, 0, c.height - side, side, side, 0, 0, side, side);
        out.toBlob((b) => resolve(b), 'image/png');
      });
    },
  }), [dragBy, dragEnd, kick, mode]);

  /* pointer drag → roll; velocity from the last two moves */
  const drag = useRef(null);
  const onPointerDown = (e) => {
    if (!interactive) return;
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, t: performance.now(), vx: 0, moved: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    const now = performance.now();
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 0) { d.vx = (dx / Math.max(1, now - d.t)) * 1000; d.x = e.clientX; d.t = now; d.moved = true; dragBy(dx); }
  };
  const onPointerUp = () => { const d = drag.current; if (!d) return; drag.current = null; dragEnd(d.vx); };

  if (!hasAvatar({ avatar })) return null;
  return (
    <div
      className={`av3d ${className || ''}`}
      style={{ position: 'relative', width: viewW, height: viewH, touchAction: interactive ? 'pan-y' : 'auto', cursor: interactive ? 'grab' : 'default', ...style }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
    >
      {!!fallback && !ready && mode === 'portrait' && (
        <img src={fallback} alt="" style={{ position: 'absolute', left: 0, bottom: 0, width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
      )}
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: viewW, height: viewH, opacity: ready ? 1 : 0, transition: 'opacity 160ms' }} />
    </div>
  );
});

export default Avatar3D;
export { liveViewSize };
